import React, { useState, useEffect } from "react";
import { usePaginated } from "../hooks";
import { api } from "../api";
import { SC, PIPELINE, PCOL, I } from "../utils";
import QuickTaskModal from "../clients/QuickTaskModal";
import {
  useToast,
  useConfirm,
  Btn,
  Card,
  Icon,
  Spinner,
  ApiError,
  Empty,
  Modal,
  Badge,
  Pages,
  Inp,
  Sel,
  Txa,
} from "../ui";

// ─── LEAD FORM ────────────────────────────────────────────────────────────────
function LeadForm({ initial = {}, onSubmit, onClose, saving }) {
  const [f, sf] = useState({
    name: initial.name || "",
    email: initial.email || "",
    phone: initial.phone || "",
    country: initial.country || "",
    interestedCountry: initial.interestedCountry?.join(", ") || "",
    source: initial.source || "",
    status: initial.status || "new",
    notes: initial.notes || "",
  });
  const set = (k) => (e) => sf((p) => ({ ...p, [k]: e.target.value }));

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({
          ...f,
          interestedCountry: f.interestedCountry
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean),
        });
      }}
      className="space-y-4"
    >
      <div className="grid grid-cols-2 gap-4">
        <Inp
          label="Name *"
          placeholder="John Smith"
          value={f.name}
          onChange={set("name")}
          required
        />
        <Inp
          label="Email"
          type="email"
          placeholder="john@email.com"
          value={f.email}
          onChange={set("email")}
        />
        <Inp
          label="Phone"
          placeholder="+234..."
          value={f.phone}
          onChange={set("phone")}
        />
        <Inp
          label="Country"
          placeholder="Nigeria"
          value={f.country}
          onChange={set("country")}
        />
        <Inp
          label="Interested Countries"
          placeholder="Dubai, Maldives, Turkey"
          value={f.interestedCountry}
          onChange={set("interestedCountry")}
          className="col-span-2"
        />
        <Sel
          label="Source"
          value={f.source}
          onChange={set("source")}
          options={[
            { value: "website", label: "Website" },
            { value: "referral", label: "Referral" },
            { value: "social_media", label: "Social Media" },
            { value: "email", label: "Email" },
            { value: "walk_in", label: "Walk-in" },
            { value: "other", label: "Other" },
          ]}
        />
        <Sel
          label="Status"
          value={f.status}
          onChange={set("status")}
          options={[
            { value: "new", label: "New" },
            { value: "contacted", label: "Contacted" },
            { value: "qualified", label: "Qualified" },
            { value: "proposal_sent", label: "Proposal Sent" },
            { value: "converted", label: "Converted" },
            { value: "lost", label: "Lost" },
          ]}
        />
      </div>
      <Txa
        label="Notes"
        placeholder="Any relevant details…"
        value={f.notes}
        onChange={set("notes")}
      />
      <div className="flex gap-3 pt-2">
        <Btn
          type="submit"
          variant="primary"
          className="flex-1"
          loading={saving}
        >
          Save Lead
        </Btn>
        <Btn type="button" variant="secondary" onClick={onClose}>
          Cancel
        </Btn>
      </div>
    </form>
  );
}

// ─── LEAD TASKS MODAL ─────────────────────────────────────────────────────────
// View + create real CRM tasks linked to a lead (via leadId).
function LeadTasksModal({ lead, onClose }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  const load = () => {
    setLoading(true);
    api
      .get(`/crm/tasks/by-lead/${lead._id}`)
      .then((res) => setTasks(Array.isArray(res) ? res : res?.data || []))
      .catch(() => setTasks([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, [lead._id]);

  const dot = (p) =>
    p === "high" ? "bg-rose-500" : p === "medium" ? "bg-amber-400" : "bg-emerald-400";

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ background: "rgba(10,15,30,0.6)", backdropFilter: "blur(8px)" }}
      >
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col">
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
            <div>
              <h2 className="text-sm font-black text-slate-800">Tasks</h2>
              <p className="text-xs text-slate-400">For {lead.name}</p>
            </div>
            <button
              onClick={onClose}
              className="w-7 h-7 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-400"
            >
              <Icon d={I.x || "M6 18L18 6M6 6l12 12"} size={13} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-2">
            {loading ? (
              <p className="text-center py-6 text-xs text-slate-400">Loading…</p>
            ) : !tasks.length ? (
              <div className="text-center py-8">
                <p className="text-sm font-semibold text-slate-400 mb-1">No tasks yet</p>
                <p className="text-xs text-slate-400">Create one to assign team members.</p>
              </div>
            ) : (
              tasks.map((t) => (
                <div key={t._id} className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100">
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${dot(t.priority)}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-700 truncate">{t.taskTitle}</p>
                    <p className="text-[10px] text-slate-400">
                      {t.category && `${t.category} · `}
                      {t.dueDate ? new Date(t.dueDate).toLocaleDateString("en-GB") : "No due date"}
                    </p>
                  </div>
                  <span className="text-[10px] font-semibold text-slate-400 capitalize flex-shrink-0">
                    {String(t.status || "").replace(/_/g, " ")}
                  </span>
                </div>
              ))
            )}
          </div>

          <div className="px-6 py-4 border-t border-slate-100">
            <Btn variant="primary" className="w-full" onClick={() => setAdding(true)}>
              <Icon d={I.plus} size={14} /> Add Task
            </Btn>
          </div>
        </div>
      </div>

      {adding && (
        <QuickTaskModal
          leadId={lead._id}
          clientName={lead.name}
          onDone={() => {
            setAdding(false);
            load();
          }}
          onClose={() => setAdding(false)}
        />
      )}
    </>
  );
}

// ─── LEADS TAB ────────────────────────────────────────────────────────────────
export function LeadsTab() {
  const toast = useToast();
  const confirm = useConfirm();
  const {
    data: leads,
    total,
    page,
    totalPages,
    loading,
    error,
    setPage,
    refetch,
  } = usePaginated("/crm/leads", 100);
  const [view, setView] = useState("list");
  const [modal, setModal] = useState(null);
  const [sel, setSel] = useState(null);
  const [saving, setSaving] = useState(false);

  const doCreate = async (f) => {
    setSaving(true);
    try {
      await api.post("/crm/leads", f);
      toast("Lead added");
      setModal(null);
      refetch();
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setSaving(false);
    }
  };
  const doEdit = async (f) => {
    setSaving(true);
    try {
      await api.patch(`/crm/leads/${sel._id}`, f);
      toast("Updated");
      setModal(null);
      refetch();
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setSaving(false);
    }
  };
  const doDelete = async (id) => {
    if (!(await confirm({ title: "Delete lead?", message: "This lead will be permanently removed.", confirmText: "Delete lead" }))) return;
    try {
      await api.delete(`/crm/leads/${id}`);
      toast("Deleted");
      refetch();
    } catch (e) {
      toast(e.message, "error");
    }
  };
  const doStatus = async (id, status) => {
    try {
      await api.patch(`/crm/leads/${id}`, { status });
      refetch();
    } catch (e) {
      toast(e.message, "error");
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-800 tracking-tight">
          Leads
        </h1>
        <div className="flex items-center gap-3">
          <div className="flex rounded-xl border border-slate-200 overflow-hidden">
            {["board", "list"].map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={`px-4 py-2 text-xs font-semibold transition-all capitalize ${view === v ? "bg-[rgb(var(--brand-rgb))] text-white" : "text-slate-500 hover:bg-slate-50"}`}
              >
                {v}
              </button>
            ))}
          </div>
          <Btn variant="primary" onClick={() => setModal("create")}>
            <Icon d={I.plus} size={16} />
            New Lead
          </Btn>
        </div>
      </div>

      {error && <ApiError msg={error} onRetry={refetch} />}
      {loading && <Spinner full />}

      {!loading && !error && view === "board" && (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {PIPELINE.map((stage) => {
            const sl = (leads || []).filter((l) => l.status === stage);
            return (
              <div key={stage} className="flex-shrink-0 w-64">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-500 capitalize">
                    {stage.replace(/_/g, " ")}
                  </span>
                  <span className="w-5 h-5 rounded-full bg-slate-100 text-slate-500 text-xs flex items-center justify-center font-bold">
                    {sl.length}
                  </span>
                </div>
                <div className="space-y-2">
                  {sl.map((l) => (
                    <div
                      key={l._id}
                      className={`p-3 rounded-xl border-l-4 ${PCOL[stage]} cursor-pointer hover:shadow-sm transition-shadow`}
                      onClick={() => {
                        setSel(l);
                        setModal("edit");
                      }}
                    >
                      <p className="font-semibold text-slate-800 text-sm mb-1">
                        {l.name}
                      </p>
                      <p className="text-xs text-slate-400 mb-2">
                        {l.country} · {l.source?.replace(/_/g, " ")}
                      </p>
                      {l.interestedCountry?.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {l.interestedCountry.map((c) => (
                            <span
                              key={c}
                              className="text-xs bg-white/70 text-slate-600 px-1.5 py-0.5 rounded-md"
                            >
                              {c}
                            </span>
                          ))}
                        </div>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSel(l);
                          setModal("tasks");
                        }}
                        className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-[rgb(var(--brand-rgb))] hover:underline"
                      >
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                        </svg>
                        Tasks
                      </button>
                    </div>
                  ))}
                  {!sl.length && (
                    <div className="h-14 rounded-xl border-2 border-dashed border-slate-200 flex items-center justify-center text-xs text-slate-300">
                      Empty
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!loading && !error && view === "list" && (
        <>
          {!(leads || []).length ? (
            <Empty
              icon="leads"
              title="No leads yet"
              sub="Add your first lead"
            />
          ) : (
            <div className="space-y-2">
              {(leads || []).map((l) => (
                <Card key={l._id} className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-400 to-indigo-500 flex items-center justify-center text-white font-bold flex-shrink-0">
                      {l.name?.charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <p className="font-semibold text-slate-800 text-sm">
                          {l.name}
                        </p>
                        {l.source && (
                          <Badge
                            label={l.source.replace(/_/g, " ")}
                            colorClass="bg-slate-100 text-slate-500"
                          />
                        )}
                      </div>
                      <p className="text-xs text-slate-400">
                        {l.email} · {l.phone} · {l.country}
                      </p>
                      {l.interestedCountry?.length > 0 && (
                        <p className="text-xs text-[rgb(var(--brand-rgb))] font-medium mt-0.5">
                          → {l.interestedCountry.join(", ")}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <select
                        value={l.status}
                        onChange={(e) => doStatus(l._id, e.target.value)}
                        className="text-xs px-2 py-1.5 rounded-lg border border-slate-200 outline-none focus:ring-2 focus:ring-[rgb(var(--brand-rgb)/0.3)]"
                      >
                        {PIPELINE.map((s) => (
                          <option key={s} value={s}>
                            {s.replace(/_/g, " ")}
                          </option>
                        ))}
                      </select>
                      <Btn
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSel(l);
                          setModal("tasks");
                        }}
                        title="Tasks"
                      >
                        <Icon d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" size={13} />
                      </Btn>
                      <Btn
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSel(l);
                          setModal("edit");
                        }}
                      >
                        <Icon d={I.edit} size={13} />
                      </Btn>
                      <Btn
                        variant="danger"
                        size="sm"
                        onClick={() => doDelete(l._id)}
                      >
                        <Icon d={I.trash} size={13} />
                      </Btn>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
          <Pages
            page={page}
            totalPages={totalPages}
            total={total}
            onPage={setPage}
            loading={loading}
          />
        </>
      )}

      {modal === "create" && (
        <Modal title="Add New Lead" onClose={() => setModal(null)}>
          <LeadForm
            onSubmit={doCreate}
            onClose={() => setModal(null)}
            saving={saving}
          />
        </Modal>
      )}
      {modal === "edit" && sel && (
        <Modal title="Edit Lead" onClose={() => setModal(null)}>
          <LeadForm
            initial={sel}
            onSubmit={doEdit}
            onClose={() => setModal(null)}
            saving={saving}
          />
        </Modal>
      )}
      {modal === "tasks" && sel && (
        <LeadTasksModal lead={sel} onClose={() => setModal(null)} />
      )}
    </div>
  );
}